/**
 * Cuentas y amigos: tipos y reglas compartidos por el cliente y el servidor.
 *
 * - Registro / inicio de sesión por HTTP (`/api/register`, `/api/login`,
 *   `/api/logout`, `/api/me`): devuelven un token de sesión.
 * - Sala Colyseus `social` (una para todos): con el token, el cliente queda
 *   "en línea"; recibe su `SocialState` (amigos con su estado, solicitudes) y
 *   envía `SocialCommand`s (estado, solicitudes, invitaciones, mensajes).
 * - Las salas de partida aceptan el token en las opciones de unión: el nombre
 *   del jugador pasa a ser el de su cuenta.
 */
import type { HeroId } from './types.ts';

export const SOCIAL_ROOM_NAME = 'social';
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 16;
export const PASSWORD_MIN = 6;
export const PASSWORD_MAX = 72;
export const DM_MAX = 300;

export interface SocialUser {
  id: number;
  username: string;
}

/** Qué está haciendo un jugador (lo informa su propio cliente). */
export type PresenceState = 'menu' | 'lobby' | 'match' | 'offline-match';

export interface PresenceStatus {
  state: PresenceState;
  /** código de la sala online (lobby/partida): permite unirse */
  roomId?: string;
  /** modo de la partida o de la sala (p. ej. "ARAM 2v2") */
  mode?: string;
  heroId?: HeroId;
}

export interface FriendEntry extends SocialUser {
  online: boolean;
  /** sólo si está en línea */
  status?: PresenceStatus;
}

export interface SocialState {
  me: SocialUser;
  friends: FriendEntry[];
  /** solicitudes que me han enviado */
  incoming: SocialUser[];
  /** solicitudes que he enviado y siguen pendientes */
  outgoing: SocialUser[];
}

/** Mensajes del cliente a la sala social (mensaje `social`). */
export type SocialCommand =
  | { type: 'status'; status: PresenceStatus }
  | { type: 'request'; username: string }
  | { type: 'respond'; userId: number; accept: boolean }
  | { type: 'cancel'; userId: number }
  | { type: 'remove'; userId: number }
  | { type: 'invite'; userId: number; roomId: string; mode: string }
  | { type: 'dm'; userId: number; text: string };

/** Invitación a una sala (mensaje `invite`, servidor → cliente). */
export interface SocialInvite {
  from: SocialUser;
  roomId: string;
  mode: string;
}

/** Mensaje privado (mensaje `dm`, servidor → los dos extremos). */
export interface DirectMessage {
  from: SocialUser;
  toId: number;
  text: string;
  at: number;
}

export interface AuthResponse {
  token: string;
  user: SocialUser;
}

/** Animales para los nombres de invitado (siempre en inglés: "Fox4821"). */
export const GUEST_ANIMALS: readonly string[] = [
  'Fox', 'Wolf', 'Tiger', 'Lion', 'Bear', 'Eagle', 'Falcon', 'Owl', 'Panda', 'Koala',
  'Dolphin', 'Shark', 'Octopus', 'Whale', 'Penguin', 'Raccoon', 'Squirrel', 'Rabbit', 'Hare', 'Deer',
  'Moose', 'Bison', 'Bull', 'Horse', 'Zebra', 'Giraffe', 'Elephant', 'Rhino', 'Hippo', 'Gorilla',
  'Monkey', 'Lynx', 'Panther', 'Jaguar', 'Puma', 'Cheetah', 'Hyena', 'Jackal', 'Badger', 'Otter',
  'Beaver', 'Hedgehog', 'Bat', 'Raven', 'Parrot', 'Toucan', 'Flamingo', 'Pelican', 'Swan', 'Duck',
  'Rooster', 'Iguana', 'Chameleon', 'Turtle', 'Crocodile', 'Caiman', 'Cobra', 'Frog', 'Salamander', 'Scorpion',
  'Crab', 'Lobster', 'Jellyfish', 'Seal', 'Walrus', 'Manatee', 'Llama', 'Alpaca', 'Camel', 'Yak',
];

/**
 * Nombre de invitado: animal + número de 4 dígitos ("Zorro4821"). `random`
 * devuelve [0, 1) (inyectable para los tests); `taken` descarta nombres ya
 * usados (en la sala o por una cuenta registrada).
 */
export function guestName(random: () => number = Math.random, taken: (name: string) => boolean = () => false): string {
  let name = '';
  for (let attempt = 0; attempt < 50; attempt++) {
    const animal = GUEST_ANIMALS[Math.floor(random() * GUEST_ANIMALS.length) % GUEST_ANIMALS.length];
    const number = 1000 + Math.floor(random() * 9000);
    name = `${animal}${number}`;
    if (!taken(name)) return name;
  }
  return name;
}

/**
 * Mensaje para el jugador que el cliente traduce a su idioma (ver `tr()` en el
 * cliente): `i18n:["plantilla en español", {params}]`. El servidor nunca manda
 * frases ya traducidas, así cada jugador las ve en el suyo.
 */
export function msg(template: string, params?: Record<string, string | number>): string {
  return `i18n:${JSON.stringify(params ? [template, params] : [template])}`;
}

/** Error legible de una regla de nombre de usuario (o null si vale). */
export function usernameError(name: unknown): string | null {
  if (typeof name !== 'string') return msg('Escribe un nombre de usuario');
  if (name.length < USERNAME_MIN || name.length > USERNAME_MAX) {
    return msg('El nombre de usuario debe tener entre {min} y {max} caracteres', { min: USERNAME_MIN, max: USERNAME_MAX });
  }
  if (!/^[A-Za-z0-9_]+$/.test(name)) return msg('Usa sólo letras sin tilde, números y guion bajo (_)');
  return null;
}

export function passwordError(password: unknown): string | null {
  if (typeof password !== 'string' || password.length < PASSWORD_MIN) {
    return msg('La contraseña debe tener al menos {min} caracteres', { min: PASSWORD_MIN });
  }
  if (password.length > PASSWORD_MAX) return msg('La contraseña no puede pasar de {max} caracteres', { max: PASSWORD_MAX });
  return null;
}

/** Normaliza un estado recibido del cliente (el servidor no confía en lo que llega). */
export function sanitizePresence(raw: unknown): PresenceStatus {
  const value = (raw ?? {}) as Record<string, unknown>;
  const states: PresenceState[] = ['menu', 'lobby', 'match', 'offline-match'];
  const state = states.includes(value.state as PresenceState) ? (value.state as PresenceState) : 'menu';
  const status: PresenceStatus = { state };
  if ((state === 'lobby' || state === 'match') && typeof value.roomId === 'string' && /^[A-Za-z0-9_-]{1,32}$/.test(value.roomId)) {
    status.roomId = value.roomId;
  }
  if (typeof value.mode === 'string') status.mode = value.mode.slice(0, 40);
  if (typeof value.heroId === 'string') status.heroId = value.heroId.slice(0, 16) as HeroId;
  return status;
}
